import React, { useEffect, useMemo, useState } from "react";
import { Card } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Row } from "react-table";
import { useStateMachine } from "little-state-machine";

import ListViewTour from "components/tours/listView/listViewTour";
import GlobalTableSearch from "components/organisms/DataTable/components/GlobalTableSearch";
import DataTable from "components/organisms/DataTable/DataTable";
import PillTabs from "components/atoms/PillTabs/PillTabs";
import RefreshCache from "components/molecules/RefreshCache/RefreshCache";
import Filter from "components/organisms/Filter/Filter";
import ColumnFilter from "components/organisms/DataTable/components/ColumnFilter";
import usePermissions from "hooks/usePermissions";
import useProviderInvoiceFilters from "hooks/filters/useProviderInvoiceFilters";
import { updateShipmentIdAndTitle } from "store/store";
import { getInvoiceStatusCount, getInvoices } from "api";
import errorToast from "helpers/errorToast";
import { useGetInvoicelist } from "api/queries/invoice/useGetInvoiceList";
import useTableQueryRequest from "hooks/useTableQueryRequest";
import useColumnOrderPersistor from "hooks/useColumnOrderPersistor";
import getInvoiceColumns from "./InvoiceColumns";
import { ProviderInvoiceAdditionalColumns } from "../../../constants";
import DeclinedInvoices from "./components/DeclinedInvoices/DeclinedInvoices";

const DISPLAY_STATUS = {
  ALL: "",
  UNSUBMITTED: "Unsubmitted",
  AWAITING_AUDIT: "awaiting_audit",
  APPROVED: "Approved",
  DECLINED: "Declined",
  REMOVED: "Removed, Auto_Removed",
};

type StatusCount = {
  all: number;
  unsubmitted: number;
  awaiting_audit: number;
  approved: number;
  declined: number;
  removed: number;
};

const getHiddenColumns = () => {
  const hiddenColumns = localStorage.getItem("provider_invoices_hidden_columns");
  if (hiddenColumns) return JSON.parse(hiddenColumns);
  localStorage.setItem("provider_invoices_hidden_columns", JSON.stringify(ProviderInvoiceAdditionalColumns));
  return ProviderInvoiceAdditionalColumns;
};

function Invoices() {
  const [searchParams, setSearchParams] = useSearchParams();
  const status: string = searchParams.get("status") || "";
  const queryPage = +(searchParams.get("page") || 0);
  const intl = useIntl();
  const { permissions } = usePermissions();
  const navigate = useNavigate();
  const { actions } = useStateMachine({ updateShipmentIdAndTitle });
  const { config: filterConfig } = useProviderInvoiceFilters();
  const { persistedColumnOrder, onColumnOrderChange } = useColumnOrderPersistor({ moduleKey: "invoice" });
  const columns = useMemo(() => getInvoiceColumns(intl, navigate), []);
  const [refreshToken, setRefreshToken] = useState(false);
  const { instance, fetching, totalData, dataUpdatedAt, refetchEndpoint } = useTableQueryRequest(
    useGetInvoicelist,
    columns,
    {
      hiddenColumns: getHiddenColumns(),
      columnOrder: persistedColumnOrder,
      params: (tableInstance: any) => {
        let sortByKey;
        if (!status) {
          sortByKey = "total_invoice_amount_all";
        } else if (status === "Unsubmitted") {
          sortByKey = "total_invoice_amount_unsubmitted";
        } else {
          sortByKey = "total_invoice_amount";
        }
        return {
          status,
          ...(refreshToken && { refresh_token: refreshToken }),
          properties: getHiddenColumns().join(","),
          ...(tableInstance?.state?.sortBy?.[0]?.id === "total_invoice_amount" && {
            sort_by: `${sortByKey}:${tableInstance?.state?.sortBy?.[0]?.desc ? "DESC" : "ASC"}`,
          }),
        };
      },
      filters: (globalFilter) => ({
        ...(globalFilter.query?.length && { query: globalFilter.query }),
        ...(globalFilter.display_status?.length && { status: globalFilter.display_status.join(",") }),
        ...(globalFilter.freight_mode?.length && { freight_mode: globalFilter.freight_mode.join(",") }),
        ...(globalFilter.shipper?.length && { shipper: globalFilter.shipper.join(",") }),
        ...(globalFilter.team?.length && { team: globalFilter.team.join(",") }),
      }),
      // deps: [status],
    },
  );
  const [declinedInvoices, setDeclinedInvoices] = useState<any>([]);

  const [isFetchingStatusCount, setIsFetchingStatusCount] = useState(false);
  const [statusCount, setStatusCount] = useState<StatusCount>({
    all: 0,
    unsubmitted: 0,
    awaiting_audit: 0,
    approved: 0,
    declined: 0,
    removed: 0,
  });

  useEffect(() => {
    if (!JSON.parse(permissions || "{}").permissions.invoicing.view_invoices) navigate("/access-denied");
  }, []);

  useEffect(() => {
    instance.gotoPage(queryPage ? queryPage - 1 : 0);
  }, [queryPage]);

  useEffect(() => {
    if (instance.state.globalFilter.display_status?.length) {
      searchParams.delete("status");
      setSearchParams(searchParams);
    }
  }, [instance.state.globalFilter.display_status]);

  const fetchInvoiceStatusCount = () => {
    setIsFetchingStatusCount(true);
    getInvoiceStatusCount()
      .then((res) => setStatusCount(res.data))
      .catch((err) => errorToast(err))
      .finally(() => setIsFetchingStatusCount(false));
  };

  useEffect(() => {
    getInvoices({ params: { status: "declined", page_size: "max" } })
      .then((res) => setDeclinedInvoices(res.data))
      .catch((err) => errorToast(err));
    fetchInvoiceStatusCount();
  }, []);

  const handleStatusChange = (eventKey: string | null) => {
    if (!eventKey) {
      searchParams.delete("status");
    } else {
      searchParams.set("status", eventKey || "");
    }
    searchParams.set("page", "1");
    setSearchParams(searchParams);
    instance.setGlobalFilter((prev: any) => ({
      ...prev,
      display_status: [],
    }));
  };

  const handleRowClick = (row: Row<any>, e: React.MouseEvent<HTMLTableRowElement, MouseEvent>) => {
    const reference = row.original?.shipment_reference;
    const title = row.original?.shipment_title;
    actions.updateShipmentIdAndTitle({
      shipmentId: reference,
      shipmentTitle: title,
    });
    if (e.ctrlKey || e.metaKey) window.open(`/shipment/${reference}/billing`, "_blank");
    else navigate(`/shipment/${reference}/billing`);
  };

  const handleRefresh = () => {
    if (!refreshToken) setRefreshToken(true);
    else {
      refetchEndpoint();
    }
  };

  return (
    <>
      <ListViewTour />
      {declinedInvoices?.results?.length > 0 && (
        <Card body className="shadow-none quote-container mb-4">
          <DeclinedInvoices declinedInvoices={declinedInvoices} />
        </Card>
      )}
      <Card body className="shadow-none quote-container">
        <div className="d-flex justify-content-between">
          <div className="d-flex flex-wrap align-items-center mb-4">
            <h3 className="mr-3">
              <FormattedMessage id="general.invoices" defaultMessage="Invoices" />
            </h3>
            <GlobalTableSearch
              currentFilters={instance.state.globalFilter}
              setFilters={instance.setGlobalFilter}
              filterKey="query"
              eventContext="Invoice List"
            />
          </div>
          <RefreshCache
            dataUpdatedAt={dataUpdatedAt}
            onRefresh={() => {
              fetchInvoiceStatusCount();
              handleRefresh();
            }}
            isLoading={fetching || isFetchingStatusCount}
          />
        </div>
        <DataTable
          tableInstance={instance}
          totalData={totalData}
          isLoading={fetching}
          onRowClick={handleRowClick}
          onColumnOrderChange={onColumnOrderChange}
          headerLeft={
            <PillTabs
              id="invoice-statuses"
              activeKey={status}
              onSelect={handleStatusChange}
              tabItems={[
                {
                  eventKey: DISPLAY_STATUS.ALL,
                  title: `${intl.formatMessage({ id: "tabs.all", defaultMessage: "All" })} (${
                    isFetchingStatusCount ? "-" : statusCount.all
                  })`,
                },
                {
                  eventKey: DISPLAY_STATUS.UNSUBMITTED,
                  title: `${intl.formatMessage({ id: "general.unsubmitted", defaultMessage: "Unsubmitted" })} (${
                    isFetchingStatusCount ? "-" : statusCount.unsubmitted
                  })`,
                },
                {
                  eventKey: DISPLAY_STATUS.AWAITING_AUDIT,
                  title: `${intl.formatMessage({ id: "tabs.awaitingAudit", defaultMessage: "Awaiting Audit" })} (${
                    isFetchingStatusCount ? "-" : statusCount.awaiting_audit
                  })`,
                },
                {
                  eventKey: DISPLAY_STATUS.APPROVED,
                  title: `${intl.formatMessage({ id: "tabs.approved", defaultMessage: "Approved" })} (${
                    isFetchingStatusCount ? "-" : statusCount.approved
                  })`,
                },
                {
                  eventKey: DISPLAY_STATUS.REMOVED,
                  title: `${intl.formatMessage({ id: "tabs.removed", defaultMessage: "Removed" })} (${
                    isFetchingStatusCount ? "-" : statusCount.removed
                  })`,
                },
              ]}
            />
          }
          headerRight={
            <div className="d-flex align-items-center">
              <Filter
                presetType="invoice"
                eventContext="Invoice List"
                currentFilters={instance.state.globalFilter}
                setFilters={instance.setGlobalFilter}
                config={filterConfig}
              />
              <div className="ml-3">
                <ColumnFilter columns={instance.allColumns} eventContext="Invoice List" listViewName="provider_invoices" />
              </div>
            </div>
          }
          withoutBody
        />
      </Card>
    </>
  );
}

export default Invoices;
